import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useCompetitorManagement } from '../hooks/useCompetitorManagement';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import SuggestedCompetitorsNew from '../components/SuggestedCompetitorsNew';
import SimpleRuleTemplatesNew from '../components/PricingRules/SimpleRuleTemplatesNew';
import CompetitorListNew from '../components/CompetitorListNew';
import AddCompetitorFormNew from '../components/AddCompetitorFormNew';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { usePricingRules } from '../hooks/usePricingRules';

const CompetitorManagementPageNew = () => {
  const { productId } = useParams();
  const [productInfo, setProductInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [priceStatus, setPriceStatus] = useState(null);
  const { getToken } = useAuth();
  const { rules } = usePricingRules(productId);
  const {
    competitors,
    addCompetitorLink,
    removeCompetitor,
    fetchCompetitors
  } = useCompetitorManagement(productId);

  const formatPrice = useCallback((price) => {
    if (price && !isNaN(price)) {
      const priceInTomans = price / 10;
      return priceInTomans.toLocaleString('fa-IR').replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]) + ' تومان';
    }
    return 'ناموجود';
  }, []);

  const loadProductInfo = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/basalam/products/${productId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProductInfo(response.data);
    } catch (error) {
      console.error('خطا در دریافت اطلاعات محصول:', error);
      toast.error('خطا در دریافت اطلاعات محصول');
    }
  }, [productId, getToken]);

  const handleAnalyzeNow = async () => {
    try {
      setIsAnalyzing(true);
      const token = getToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/competitors/products/${productId}/analyze-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data && response.data.alerts) {
        setPriceStatus({
          hasAlert: response.data.alerts.length > 0,
          alerts: response.data.alerts
        });
      }

      await Promise.all([
        fetchCompetitors(),
        loadProductInfo()
      ]);

      toast.success('تحلیل قیمت با موفقیت انجام شد');
    } catch (error) {
      console.error('خطا در تحلیل قیمت:', error);
      toast.error('خطا در تحلیل قیمت');
    } finally {
      setIsAnalyzing(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const initializePage = async () => {
      setIsLoading(true);
      try {
        await loadProductInfo();
      } catch (error) {
        console.error('خطا در بارگذاری:', error);
        toast.error('خطا در بارگذاری اطلاعات');
      } finally {
        setIsLoading(false);
      }
    };
    initializePage();
  }, [loadProductInfo]);

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Product Header با shadcn */}
      {productInfo && (
        <Card className="p-6 shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-start gap-4">
                {/* Product Image */}
                {productInfo.photo?.md && (
                  <img
                    src={productInfo.photo.md}
                    alt={productInfo.title}
                    className="w-20 h-20 rounded-lg object-cover shadow-md"
                    onError={(e) => {
                      e.target.src = '/placeholder.png';
                    }}
                  />
                )}
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-bold mb-2">{productInfo.title}</h1>
                  <div className="flex flex-wrap items-center gap-3">
                    <Badge variant="outline" className="text-sm">
                      💰 {formatPrice(productInfo.price)}
                    </Badge>
                    {productInfo.inventory && (
                      <Badge variant="secondary" className="text-sm">
                        📦 موجودی: {productInfo.inventory}
                      </Badge>
                    )}
                    {competitors.length > 0 && (
                      <Badge className="bg-purple-600 text-sm">
                        👥 {competitors.length} رقیب
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <Button
              onClick={handleAnalyzeNow}
              disabled={isAnalyzing || rules.length === 0}
              size="lg"
              className="w-full md:w-auto"
            >
              <ArrowPathIcon className={`w-5 h-5 ml-2 ${isAnalyzing ? 'animate-spin' : ''}`} />
              {isAnalyzing ? 'در حال تحلیل...' : 'تحلیل قیمت'}
            </Button>
          </div>
        </Card>
      )}

      {/* رقبای پیشنهادی - اول */}
      <div>
        <SuggestedCompetitorsNew
          productId={productId}
          onCompetitorsAdded={fetchCompetitors}
        />
      </div>

      {/* لیست رقبای فعلی */}
      {competitors.length > 0 && (
        <div>
          <CompetitorListNew
            competitors={competitors}
            removeCompetitor={removeCompetitor}
            formatPrice={formatPrice}
          />
        </div>
      )}

      {/* قوانین قیمت‌گذاری */}
      <div>
        <SimpleRuleTemplatesNew
          productId={productId}
          priceStatus={priceStatus}
          onRuleApplied={handleAnalyzeNow}
          hasCompetitors={competitors.length > 0}
        />
      </div>

      {/* افزودن دستی رقیب */}
      <Card className="p-6">
        <details>
          <summary className="cursor-pointer font-medium text-gray-700 hover:text-gray-900 transition flex items-center gap-2">
            <span className="text-lg">➕</span>
            <span>افزودن دستی لینک رقیب (اختیاری)</span>
          </summary>
          <div className="mt-4">
            <Separator className="mb-4" />
            <AddCompetitorFormNew addCompetitorLink={addCompetitorLink} />
          </div>
        </details>
      </Card>
    </div>
  );
};

export default CompetitorManagementPageNew;
